import { ArrowLeft, ArrowRight, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileLayout } from "../components/layout/MobileLayout.jsx";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../components/ui/Avatar.jsx";
import { Badge } from "../components/ui/Badge.jsx";
import { Button } from "../components/ui/Button.jsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/Card.jsx";
import { Input } from "../components/ui/Input.jsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/Tabs.jsx";
import { useToast } from "../hooks/useToast.js";
import { fetchUnassignedLeads, updateLead } from "../services/leadService.js";
import { fetchUsersByRole } from "../services/teamService.js";
import useDebounce from "../hooks/useDebouncer.js";
import { getCurrentUser } from "../services/authService.js";

export default function Team() {
  const [activeTab, setActiveTab] = useState("team");
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedRep, setSelectedRep] = useState(null);
  const [team, setTeam] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [teamPage, setTeamPage] = useState(1);
  const [teamTotalPages, setTeamTotalPages] = useState(1);
  const [teamLoading, setTeamLoading] = useState(false);
  const [unassigned, setUnassigned] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 1000);
  const [currentUser, setCurrentUser] = useState(null);

  const handleLeadClick = (memberId) => navigate(`/team/${memberId}`);

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const res = await getCurrentUser();
        if (res?.success) setCurrentUser(res.user);
      } catch {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to fetch current user",
        });
      }
    };
    loadCurrentUser();
  }, []);

  // useEffect(() => {
  //   const loadAllData = async () => {
  //     setTeamLoading(true);
  //     setIsLoading(true);
  //     setError("");

  //     try {
  //       const res = await fetchUsersByRole("sales_person", {
  //         page: teamPage,
  //         limit: 10,
  //         search: debouncedSearchQuery,
  //       });

  //       const users = Array.isArray(res?.users || res?.data)
  //         ? res?.users || res?.data
  //         : [];

  //       setTotalUsers(res?.total || users.length || 0);
  //       setTeamTotalPages(res?.totalPages || 1);
  //       setTeam(users);

  //       const leadsRes = await fetchUnassignedLeads({
  //         page,
  //         limit: 10,
  //         search: debouncedSearchQuery,
  //       });
  //       const leadsArray = Array.isArray(leadsRes?.leads) ? leadsRes.leads : [];
  //       setUnassigned(leadsArray);
  //       setTotalPages(leadsRes?.totalPages || 1);
  //     } catch (err) {
  //       setError(err?.message || "Failed to load team or leads");
  //       setTeam([]);
  //       setUnassigned([]);
  //     } finally {
  //       setTeamLoading(false);
  //       setIsLoading(false);
  //     }
  //   };

  //   loadAllData();
  // }, [teamPage, page, debouncedSearchQuery]);

  useEffect(() => {
    if (activeTab !== "team") return;

    const loadTeam = async () => {
      setTeamLoading(true);
      setError("");

      try {
        const res = await fetchUsersByRole("sales_person", {
          page: teamPage,
          limit: 10,
          search: debouncedSearchQuery,
        });

        const users = Array.isArray(res?.users || res?.data)
          ? res?.users || res?.data
          : [];

        setTotalUsers(res?.total || users.length || 0);
        setTeamTotalPages(res?.totalPages || 1);
        setTeam(users);
      } catch (err) {
        setError(err?.message || "Failed to load team");
        setTeam([]);
      } finally {
        setTeamLoading(false);
      }
    };

    loadTeam();
  }, [teamPage, debouncedSearchQuery, activeTab]);

  useEffect(() => {
    if (activeTab !== "assign") return;

    const loadUnassigned = async () => {
      setIsLoading(true);
      setError("");

      try {
        const leadsRes = await fetchUnassignedLeads({
          page,
          limit: 10,
          search: debouncedSearchQuery,
        });

        const leadsArray = Array.isArray(leadsRes?.leads) ? leadsRes.leads : [];

        setUnassigned(leadsArray);
        setTotalPages(leadsRes?.totalPages || 1);
      } catch (err) {
        setError(err?.message || "Failed to load unassigned leads");
        setUnassigned([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadUnassigned();
  }, [page, debouncedSearchQuery, activeTab]);

  useEffect(() => {
    if (activeTab === "team") {
      setTeamPage(1);
    } else if (activeTab === "assign") {
      setPage(1);
    }
  }, [activeTab]);

  const toggleLeadSelection = (leadId) => {
    setSelectedLeads((prev) => (prev.includes(leadId) ? [] : [leadId]));
  };

  const assignLeads = async () => {
    if (selectedLeads.length === 0 || !selectedRep) return;
    try {
      await Promise.all(
        selectedLeads.map((leadId) =>
          updateLead(leadId, { agent_assigned: selectedRep }),
        ),
      );
      setSelectedLeads([]);
      setSelectedRep(null);

      const res = await fetchUnassignedLeads({
        page,
        limit: 10,
        search: debouncedSearchQuery,
      });
      setUnassigned(Array.isArray(res?.leads) ? res.leads : []);

      toast({
        title: "Success",
        description: `${selectedLeads.length} lead(s) assigned successfully.`,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to assign leads.",
        variant: "destructive",
      });
    }
  };

  const statRows = [
    { label: "Total Leads", key: "total_leads", color: "text-foreground" },
    { label: "Won", key: "won", color: "text-emerald-600" },
    { label: "Lost", key: "lost", color: "text-rose-500" },
    { label: "Call Back", key: "call_back", color: "text-purple-500" },
    { label: "Meeting", key: "meeting", color: "text-sky-500" },
    {
      label: "Quotation Created",
      key: "quotation_created",
      color: "text-amber-500",
    },
    {
      label: "Quotation Send",
      key: "quotation_send",
      color: "text-orange-500",
    },
    {
      label: "Quotation Revised",
      key: "quotation_revised",
      color: "text-indigo-500",
    },
  ];

  return (
    <MobileLayout userRole="business_admin">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Team Management</h1>
          <Button
            size="icon"
            className="rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391] text-white"
            onClick={() => navigate("/add-team-member")}
          >
            <Plus className="w-5 h-5" />
          </Button>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v)}
          className="space-y-4"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="team">Team</TabsTrigger>
            <TabsTrigger value="assign">Unassigned Leads</TabsTrigger>
          </TabsList>

          {/* ── Team Tab ── */}
          <TabsContent value="team" className="space-y-2 pb-4">
            <div className="relative">
              <Search className="absolute w-4 h-4 left-4 top-[50%] translate-y-[-50%] text-muted-foreground" />
              <Input
                placeholder="Search team members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 text-sm border-none rounded-full h-9 bg-muted"
              />
            </div>

            <p className="text-xs text-muted-foreground">
              Total Users: {totalUsers}
            </p>

            {teamLoading && (
              <p className="text-sm text-muted-foreground">
                Loading team data...
              </p>
            )}
            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="space-y-3">
              {team.map((member) => (
                <Card
                  key={member.id}
                  className="transition-shadow bg-white border-2 shadow-sm cursor-pointer border-blue-100/50 rounded-xl hover:shadow-md"
                  onClick={() => handleLeadClick(member.id)}
                >
                  <CardContent className="p-4">
                    {/* Name row */}
                    <div className="flex items-center gap-3 mb-4">
                      <Avatar className="w-9 h-9">
                        <AvatarImage src="/placeholder.svg" alt={member.name} />
                        <AvatarFallback className="text-sm font-bold text-blue-800">
                          {(member.name || "").trim()[0]?.toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold">{member.name || "-"}</p>
                        <p className="text-xs text-muted-foreground">
                          {member.email || ""}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 w-fit gap-x-8 gap-y-1.5 mt-1">
                      {statRows.map(({ label, key, color }) => (
                        <div
                          key={key}
                          className="flex items-center justify-between space-x-5"
                        >
                          <span className="text-[14px] text-muted-foreground whitespace-nowrap">
                            {label}:
                          </span>
                          <span
                            className={`text-[14px] font-semibold ${color}`}
                          >
                            {member[key] ?? 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {teamTotalPages > 1 && (
              <div className="flex items-center justify-between pt-2 pb-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-8 h-8 rounded-full opacity-85 backdrop-blur-xl"
                  disabled={teamPage <= 1 || teamLoading}
                  onClick={() => setTeamPage((p) => Math.max(1, p - 1))}
                >
                  <ArrowLeft />
                </Button>
                <div className="text-xs text-muted-foreground">
                  Page {teamPage} of {teamTotalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-8 h-8 rounded-full opacity-85 backdrop-blur-xl"
                  disabled={teamPage >= teamTotalPages || teamLoading}
                  onClick={() =>
                    setTeamPage((p) => Math.min(teamTotalPages, p + 1))
                  }
                >
                  <ArrowRight />
                </Button>
              </div>
            )}
          </TabsContent>

          {/* ── Assign Tab ── */}
          <TabsContent value="assign" className="space-y-4 pb-4">
            {selectedLeads.length > 0 && (
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium">
                      Assign {selectedLeads.length} leads to:
                    </h3>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedLeads([])}
                    >
                      Clear
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-2 mb-4">
                    {team
                      .concat(
                        currentUser &&
                          !team.some(
                            (m) => String(m.id) === String(currentUser.id),
                          )
                          ? [
                              {
                                ...currentUser,
                                name: `${currentUser.name} (You)`,
                              },
                            ]
                          : [],
                      )
                      .map((member) => (
                        <Button
                          key={member.id}
                          variant={
                            selectedRep === member.id ? "default" : "outline"
                          }
                          className="justify-start h-auto p-3"
                          onClick={() => setSelectedRep(member.id)}
                        >
                          <Avatar className="w-6 h-6 flex items-center justify-center mr-2">
                            <AvatarFallback className="text-xs p-4 font-bold !rounded-lg capitalize">
                              {(member.name || "U")
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <p className="font-medium text-left">{member.name}</p>
                        </Button>
                      ))}
                  </div>

                  <Button
                    className="w-full"
                    onClick={assignLeads}
                    disabled={!selectedRep}
                  >
                    Assign Leads
                  </Button>
                </CardContent>
              </Card>
            )}

            <Card className="!bg-transparent shadow-none border-none">
              <CardHeader className="py-0">
                <CardTitle className="text-lg py-0">
                  Unassigned Leads ({unassigned.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pb-4">
                {unassigned.map((lead) => (
                  <div
                    key={lead.id}
                    className={`p-3 transition-shadow bg-white border-2 shadow-sm cursor-pointer rounded-xl hover:shadow-md ${
                      selectedLeads.includes(lead.id)
                        ? "border-primary bg-primary/5"
                        : "border-blue-100/50"
                    }`}
                    onClick={() => toggleLeadSelection(lead.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium">{lead.name}</h4>
                        <Badge
                          variant="secondary"
                          className="bg-muted text-muted-foreground"
                        >
                          Unassigned
                        </Badge>
                        <p className="text-sm text-muted-foreground">
                          {lead.email || "No email"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Source: {lead.reference || "—"}
                        </p>
                      </div>
                      {selectedLeads.includes(lead.id) && (
                        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary">
                          <div className="w-2 h-2 bg-white rounded-full" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <div className="flex items-center justify-between pt-2 pb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-8 h-8 rounded-full opacity-85 backdrop-blur-xl"
                    disabled={page <= 1 || isLoading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ArrowLeft />
                  </Button>
                  <div className="text-xs text-muted-foreground">
                    Page {page} of {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-8 h-8 rounded-full opacity-85 backdrop-blur-xl"
                    disabled={page >= totalPages || isLoading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ArrowRight />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MobileLayout>
  );
}
